import { Add, Close, Remove, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import {
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { addMonths, format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { IInstallmentProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { StatusInstallmentChip } from "../../../../../shared/components/StatusInstallmentChip";
import { globalTranslateStatus } from "../../../../../shared/utils/global-translate";

interface ModalAdjustInstallmentsDueDateProps {
	isOpen: boolean;
	installments: IInstallmentProposal[];
	onClose: () => void;
	onApply: (options: { adjustments: Array<{ id: string; monthsDelta: number; amount?: string }> }) => void | Promise<void>;
}

const normalizeStatus = (status: string | undefined) => (status ?? "").trim().toLowerCase();

function maskCurrencyBRFromDigits(raw: string): string {
	const digits = (raw ?? "").replace(/\D/g, "");
	if (!digits) return "";
	let v = digits;

	// garante ao menos 3 dígitos pra ter "0,xx"
	if (v.length > 0 && v.length < 3) v = v.padStart(3, "0");

	// remove zero à esquerda se tiver mais que 3
	if (v.length > 3) v = v.replace(/^0+/, "") || "0";

	// coloca vírgula nos últimos 2
	v = v.replace(/(\d{2})$/, ",$1");

	// separador de milhar
	v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

	return v;
}

function normalizeAmountToDot(raw: string): string | null {
	const trimmed = (raw ?? "").trim();
	if (!trimmed) return null;

	const cleaned = trimmed
		.replace(/\s/g, "")
		.replace(/\./g, "")
		.replace(/,/g, ".")
		.replace(/[^0-9.]/g, "");

	if (!cleaned) return null;

	const value = Number.parseFloat(cleaned);
	if (!Number.isFinite(value) || value < 0) return null;

	return value.toFixed(2);
}

export const ModalAdjustInstallmentsDueDate = ({ isOpen, installments, onClose, onApply }: ModalAdjustInstallmentsDueDateProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [monthsDeltaById, setMonthsDeltaById] = useState<Record<string, number>>({});
	const [amountOverrideById, setAmountOverrideById] = useState<Record<string, string>>({});
	const [bulkAmount, setBulkAmount] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [initialBulkAmount, setInitialBulkAmount] = useState<string>("");

	const sortedInstallments = useMemo(() => {
		return [...(installments ?? [])].sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
	}, [installments]);

	const pendingIds = useMemo(() => {
		return sortedInstallments.filter((i) => normalizeStatus(i.status) === "pending").map((i) => i.id);
	}, [sortedInstallments]);

	const pendingIdSet = useMemo(() => new Set(pendingIds), [pendingIds]);

	const allSelected = selectedIds.length === pendingIds.length && pendingIds.length > 0;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	useEffect(() => {
		if (!isOpen) return;

		setSelectedIds([]);
		setMonthsDeltaById({});
		setAmountOverrideById({});
		setIsSubmitting(false);

		const firstPending = sortedInstallments.find((i) => normalizeStatus(i.status) === "pending");

		// inicializa o campo com o valor da primeira pendente (em BRL mascarado)
		if (firstPending?.amount != null) {
			const n = Number(firstPending.amount);
			if (Number.isFinite(n)) {
				const digits = Math.round(n * 100).toString();
				const masked = maskCurrencyBRFromDigits(digits);
				setBulkAmount(masked);
				setInitialBulkAmount(masked);
			} else {
				setBulkAmount("");
				setInitialBulkAmount("");
			}
		} else {
			setBulkAmount("");
			setInitialBulkAmount("");
		}
	}, [isOpen]);

	const toggleSelection = (id: string) => {
		if (!pendingIdSet.has(id)) return;

		setSelectedIds((prev) => {
			const isSelected = prev.includes(id);
			const next = isSelected ? prev.filter((x) => x !== id) : [...prev, id];

			// se desmarcou, limpa ajustes específicos
			if (isSelected) {
				setMonthsDeltaById((map) => {
					const nextMap = { ...map };
					delete nextMap[id];
					return nextMap;
				});
				setAmountOverrideById((map) => {
					const nextMap = { ...map };
					delete nextMap[id];
					return nextMap;
				});
			}

			return next;
		});
	};

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds([]);
			setMonthsDeltaById({});
			setAmountOverrideById({});
			return;
		}
		setSelectedIds(pendingIds);
	};

	const hasAnyChange = useMemo(() => {
		// Se algum mês mudou ou algum valor individual mudou
		if (selectedIds.some((id) => (monthsDeltaById[id] ?? 0) !== 0 || Boolean(amountOverrideById[id]))) return true;
		// Se o valor em lote mudou em relação ao inicial e há seleção
		if (selectedIds.length > 0 && bulkAmount && bulkAmount !== initialBulkAmount) return true;
		return false;
	}, [selectedIds, monthsDeltaById, amountOverrideById, bulkAmount, initialBulkAmount]);

	const normalizedBulkAmount = useMemo(() => normalizeAmountToDot(bulkAmount), [bulkAmount]);

	const handleBulkAmountChangeMasked = (masked: string) => {
		setBulkAmount(masked);

		const normalized = normalizeAmountToDot(masked);
		if (selectedIds.length === 0 || !normalized) return;

		setAmountOverrideById((prev) => {
			const next = { ...prev };
			for (const id of selectedIds) {
				if (!pendingIdSet.has(id)) continue;
				next[id] = normalized; // "1234.56"
			}
			return next;
		});
	};

	const adjustSelectedMonths = (delta: number) => {
		if (selectedIds.length === 0) return;

		setMonthsDeltaById((prev) => {
			const next = { ...prev };
			for (const id of selectedIds) {
				if (!pendingIdSet.has(id)) continue;
				next[id] = (next[id] ?? 0) + delta;
			}
			return next;
		});
	};

	const handleConfirm = async () => {
		const adjustments = selectedIds
			.map((id) => ({
				id,
				monthsDelta: monthsDeltaById[id] ?? 0,
				amount: amountOverrideById[id],
			}))
			.filter((x) => x.monthsDelta !== 0 || Boolean(x.amount));

		if (adjustments.length === 0) return;

		try {
			setIsSubmitting(true);
			await onApply({ adjustments });
			onClose();
		} finally {
			setIsSubmitting(false);
		}
	};

	const formatDue = (raw: any) => {
		if (!raw) return "—";
		// Corrige fuso: força data local (ignora UTC)
		let d: Date;
		if (raw instanceof Date) {
			d = raw;
		} else if (typeof raw === "string" && raw.length >= 10) {
			// Força yyyy-MM-dd para local
			const [y, m, day] = raw.split("T")[0].split("-").map(Number);
			if (y && m && day) {
				d = new Date(y, m - 1, day);
			} else {
				d = new Date(raw);
			}
		} else {
			d = new Date(raw);
		}
		if (Number.isNaN(d.getTime())) return "—";
		return format(d, "dd/MM/yyyy");
	};

	return (
		<Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Ajustar parcelas
				</Typography>
				<IconButton aria-label="Fechar" onClick={onClose} size="small" disabled={isSubmitting}>
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 2, maxHeight: "60vh" }}>
				{sortedInstallments.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhuma parcela disponível.
					</Typography>
				) : (
					<List dense disablePadding>
						<ListItem divider>
							<ListItemIcon>
								<Checkbox
									edge="start"
									checked={allSelected}
									indeterminate={partiallySelected}
									onChange={toggleSelectAll}
									disabled={isSubmitting || pendingIds.length === 0}
								/>
							</ListItemIcon>

							<ListItemText primary="Selecionar todas as pendentes" secondary={`${pendingIds.length} pendente(s)`} />
						</ListItem>

						{sortedInstallments.map((item) => {
							const isPending = normalizeStatus(item.status) === "pending";
							const disabled = !isPending || isSubmitting;

							const isSelected = selectedIds.includes(item.id);
							const monthsDelta = monthsDeltaById[item.id] ?? 0;
							const amountOverride = amountOverrideById[item.id]; // "1234.56"

							const hasChange = isSelected && (monthsDelta !== 0 || !!amountOverride);

							const originalAmountLabel = item.amount == null ? "—" : globalFormatCurrencyBRL(item.amount);

							const newAmountLabel = amountOverride != null ? globalFormatCurrencyBRL(amountOverride) : null;

							const originalDue = formatDue((item as any).dueDate ?? (item as any).due);
							const newDue =
								monthsDelta !== 0
									? (() => {
											const base = (item as any).dueDate ?? (item as any).due;
											// Usa a mesma lógica do formatDue para garantir data local
											let d: Date | null = null;
											if (base instanceof Date) {
												d = base;
											} else if (typeof base === "string" && base.length >= 10) {
												const [y, m, day] = base.split("T")[0].split("-").map(Number);
												if (y && m && day) {
													d = new Date(y, m - 1, day);
												} else {
													d = new Date(base);
												}
											} else if (base) {
												d = new Date(base);
											}
											if (!d || Number.isNaN(d.getTime())) return "—";
											return format(addMonths(d, monthsDelta), "dd/MM/yyyy");
										})()
									: null;

							return (
								<ListItem
									key={item.id}
									divider
									disableGutters
									sx={{
										px: 0.5,
										py: 0.5,
										minHeight: 36,
										opacity: isPending ? 1 : 0.55,
									}}
								>
									<ListItemIcon sx={{ minWidth: 32 }}>
										<Checkbox edge="start" checked={isSelected} onChange={() => toggleSelection(item.id)} disabled={disabled} size="small" />
									</ListItemIcon>

									<Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
										<Stack sx={{ flex: 1 }}>
											<Stack direction="row" alignItems="center" gap={1}>
												<Typography variant="body2" sx={{ minWidth: 70 }}>Parcela {item.number ?? "—"}</Typography>
												{isSelected && monthsDelta !== 0 && (
													<Chip
														size="small"
														sx={{
															bgcolor: monthsDelta > 0 ? "#e8f5e9" : "#ffebee",
															color: monthsDelta > 0 ? "#388e3c" : "#d32f2f",
															fontWeight: 500,
															px: 0.5,
															height: 24,
														}}
														icon={monthsDelta > 0 ? <ArrowUpward sx={{ fontSize: 16, color: "#388e3c" }} /> : <ArrowDownward sx={{ fontSize: 16, color: "#d32f2f" }} />}
														label={`${monthsDelta > 0 ? "+" : ""}${monthsDelta}m`}
													/>
												)}
											</Stack>
											<Stack direction="row" gap={1} alignItems="center" sx={{ minHeight: 20 }}>
												<Typography variant="caption" color="text.secondary">Vencimento:</Typography>
												{hasChange && newDue ? (
													<Typography component="span" variant="caption" sx={{ textDecoration: "line-through" }}>{originalDue}</Typography>
												) : null}
												<Typography component="span" variant="caption" sx={{ fontWeight: hasChange && newDue ? 700 : 400, color: hasChange && newDue ? "primary.main" : "inherit" }}>{newDue || originalDue}</Typography>
											</Stack>
											<Stack direction="row" gap={1} alignItems="center" sx={{ minHeight: 20 }}>
												<Typography variant="caption" color="text.secondary">Valor:</Typography>
												{hasChange && newAmountLabel ? (
													<Typography component="span" variant="caption" sx={{ textDecoration: "line-through" }}>{originalAmountLabel}</Typography>
												) : null}
												<Typography component="span" variant="caption" sx={{ fontWeight: hasChange && newAmountLabel ? 700 : 400, color: hasChange && newAmountLabel ? "primary.main" : "inherit" }}>{newAmountLabel || originalAmountLabel}</Typography>
											</Stack>
										</Stack>
										<StatusInstallmentChip status={item.status ?? ""} label={globalTranslateStatus(item.status ?? "")} />
									</Stack>
								</ListItem>
							);
						})}
					</List>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
					<Stack direction="row" gap={1} alignItems="center">
						<IconButton
							size="small"
							disabled={isSubmitting || selectedIds.length === 0}
							onClick={() => adjustSelectedMonths(-1)}
							sx={{
								width: 36,
								height: 36,
								border: "1px solid",
								borderColor: "divider",
								bgcolor: "action.hover",
								"&:hover": { bgcolor: "action.selected" },
								"&.Mui-disabled": { bgcolor: "action.disabledBackground" },
							}}
						>
							<Remove fontSize="small" />
						</IconButton>

						<IconButton
							size="small"
							disabled={isSubmitting || selectedIds.length === 0}
							onClick={() => adjustSelectedMonths(+1)}
							sx={{
								width: 36,
								height: 36,
								border: "1px solid",
								borderColor: "divider",
								bgcolor: "action.hover",
								"&:hover": { bgcolor: "action.selected" },
								"&.Mui-disabled": { bgcolor: "action.disabledBackground" },
							}}
						>
							<Add fontSize="small" />
						</IconButton>
					</Stack>

					<Stack direction="row" gap={1} alignItems="center">
						<TextField
							label="Valor (em lote)"
							disabled={isSubmitting || selectedIds.length === 0}
							value={bulkAmount}
							size="small"
							onChange={(e) => {
								// Aplica máscara e já propaga para selecionados
								const masked = maskCurrencyBRFromDigits(e.target.value);
								handleBulkAmountChangeMasked(masked);
							}}
							onBlur={(e) => {
								// Garante máscara ao sair do campo
								const masked = maskCurrencyBRFromDigits(e.target.value);
								setBulkAmount(masked);
							}}
							placeholder="0,00"
							sx={{ width: 160 }}
							inputProps={{ inputMode: "numeric" }}
						/>

						<Button type="button" variant="contained" onClick={handleConfirm} disabled={isSubmitting || !hasAnyChange}>
							Atualizar
						</Button>
					</Stack>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
